import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import JsonLd from '@/components/base/JsonLd';
import { testimonialList } from '@/mocks/series';
import ContactForm from './components/ContactForm';

const BRAND_IMG =
  'https://readdy.ai/api/search-image?query=Flat%20modern%20editorial%20illustration%20of%20a%20newsroom%20writing%20desk%20where%20a%20journalist%20turns%20scattered%20gossip%20notes%20into%20a%20neat%20tv%20series%20timeline%20ledger%2C%20watermelon%20slices%20and%20popcorn%20nearby%2C%20warm%20cream%20background%2C%20coral%20orange%20red%20and%20mint%20green%20watermelon%20palette%2C%20soft%20rounded%20shapes%2C%20minimal%2C%20high%20detail&width=1200&height=900&seq=about-brand&orientation=landscape';

const values = [
  { icon: 'ri-scales-3-line', title: '中立不站队', text: '只呈现事实与进程，保留多方视角，不预设立场带节奏。' },
  { icon: 'ri-stack-line', title: '按集整理', text: '把成系列的瓜料做成第1集到第N集的栏目，翻得明明白白。' },
  { icon: 'ri-search-line', title: '还原来龙去脉', text: '串联散落在各处的信息，让你一次看清前因后果与后续进展。' },
  { icon: 'ri-shield-user-line', title: '理性吃瓜', text: '站内剧情均为虚构娱乐内容，不传播隐私，不当真不当假。' },
  { icon: 'ri-emotion-laugh-line', title: '有趣表达', text: '轻松的文字与生动的连续剧设定，让吃瓜本身成为一种乐趣。' },
  { icon: 'ri-flashlight-line', title: '随热追更', text: '热门瓜局实时同步，剧情更新与最新动向第一时间跟上。' },
];

const principles = [
  '整理公开、可查证的信息与公开进程，不追逐未经证实的隐私与谣传',
  '用连续剧式的时间线代替单条碎片化信息，前因后果一目了然',
  '在整理中保留反转与双方视角，不替读者站队，也不煽动对立',
  '站内所有系列剧情均为虚构演示内容，提醒大家理性吃瓜、友善追更',
  '内容保持轻快有趣，但也守住边界，不越线、不冒犯、不做恶意揣测',
];

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      name: '关于吃瓜每日',
      inLanguage: 'zh-CN',
      description: '了解吃瓜每日的品牌介绍、定位、发展理念、内容原则与联系方式。',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
  ],
};

export default function About() {
  return (
    <Layout>
      <JsonLd data={pageSchema} />

      {/* 页头 */}
      <section className="bg-gradient-to-br from-accent-100 via-background-100 to-secondary-100 pb-12 pt-24 md:pb-16 md:pt-28">
        <div className="mx-auto w-full px-4 md:px-6">
          <div className="mx-auto w-full max-w-6xl">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1">
                <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
                <li aria-hidden="true"><i className="ri-arrow-right-s-line text-foreground-300"></i></li>
                <li className="font-medium text-foreground-700">关于我们</li>
              </ol>
            </nav>
            <h1 className="mt-4 font-heading text-3xl text-foreground-950 md:text-5xl">把吃瓜，做成一部追得下去的剧</h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
              吃瓜每日是一档专注娱乐吃瓜的连续剧式追更平台。我们相信好吃的瓜不该被淹没在信息流里，
              而该是能顺着时间线一路追下去的剧情——这是我们的初心，也是我们一直在做的事。
            </p>
          </div>
        </div>
      </section>

      {/* 品牌介绍 */}
      <section className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-14">
          <div className="order-2 lg:order-1">
            <p className="text-sm font-bold tracking-wide text-primary-600">品牌介绍</p>
            <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-4xl">谁在为你把瓜翻成连续剧</h2>
            <p className="mt-5 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
              吃瓜，本是中文互联网里最轻松的社交方式之一：大家凑到一起，看着一场又一场热闹，交换各自的看法。可热闹多了，瓜也就散了——
              常常看到一半没下文，前因后果要靠自己拼。吃瓜每日想做的那件事，就是把「吃不完、吃不明白」的瓜，
              整理成一部部有顺序、有进度、能追下去的连续剧。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
              我们只做整理者，不做裁判。用剧情更新、后续进展、第N集与系列跟踪的方式，把公开的信息按照时间线码放整齐。
              读者翻到哪里都清楚，追到哪一集都不迷路。轻松追更之余，也留一点理性，看得开心就好。
            </p>
          </div>
          <div className="order-1 lg:order-2">
            <div className="overflow-hidden rounded-3xl border border-background-200 bg-background-100">
              <img src={BRAND_IMG} alt="吃瓜每日编辑部把零散瓜料整理成连续剧时间线" className="h-full w-full object-cover object-top" />
            </div>
          </div>
        </div>
      </section>

      {/* 数据带 */}
      <section className="bg-primary-600 py-10 text-background-50 md:py-14">
        <div className="mx-auto w-full px-4 md:px-6">
          <div className="mx-auto grid w-full max-w-4xl grid-cols-1 gap-6 text-center sm:grid-cols-3">
            {[
              ['40+', '已收录的追更系列'],
              ['6 大', '吃瓜类型分类'],
              ['7×24', '随热更新不缺席'],
            ].map(([num, label]) => (
              <div key={label}>
                <p className="font-heading text-4xl text-background-50 md:text-5xl">{num}</p>
                <p className="mt-2 text-sm text-background-100/90">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 理念与价值观 */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6">
          <div className="mx-auto w-full max-w-6xl">
            <div className="text-center">
              <p className="text-sm font-bold tracking-wide text-primary-600">我们的理念</p>
              <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-4xl">追更也要有自己的价值观</h2>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {values.map((v) => (
                <div key={v.title} className="rounded-2xl border border-background-200 bg-background-50 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary-100 text-xl text-primary-600">
                    <i className={v.icon}></i>
                  </span>
                  <h3 className="mt-4 text-base font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 内容原则 */}
      <section className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <p className="text-sm font-bold tracking-wide text-primary-600">内容原则</p>
            <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-4xl">怎么整理，才配得上「追更」二字</h2>
            <ul className="mt-6 flex flex-col gap-4">
              {principles.map((p, i) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-sm text-primary-700">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="pt-1 text-sm leading-6 text-foreground-700">{p}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-background-200 bg-gradient-to-br from-primary-50 to-accent-50 p-7 md:p-9">
            <h3 className="text-lg font-bold text-foreground-950">我们想说</h3>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              吃瓜是一群人共享的热闹，也是一场需要边界感的社交。我们希望吃瓜每日提供的是轻松、清晰、不添乱的追更体验：
            </p>
            <p className="mt-4 font-serif-sc text-base leading-8 text-foreground-800">
              「好的吃瓜，是让所有人都看得开心、看得明白，而不是让谁下不来台。」
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {['轻松', '清晰', '理性', '有边界', '追得到底'].map((k) => (
                <span key={k} className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1 text-xs font-medium text-foreground-600">
                  #{k}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 用户口碑 */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6">
          <div className="mx-auto w-full max-w-6xl">
            <div className="text-center">
              <p className="text-sm font-bold tracking-wide text-primary-600">用户口碑</p>
              <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-4xl">追更人的真实感受</h2>
            </div>
            <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonialList.map((t) => (
                <figure key={t.name} className="flex h-full flex-col rounded-2xl border border-background-200 bg-background-50 p-6">
                  <div className="flex gap-0.5 text-secondary-500">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <i key={k} className="ri-star-fill text-xs"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 flex-1 text-sm leading-6 text-foreground-700">“{t.text}”</blockquote>
                  <div className="mt-5 flex items-center gap-3 border-t border-background-200/70 pt-4">
                    <img src={t.avatar} alt={`${t.name} 的头像`} className="h-10 w-10 rounded-full object-cover" />
                    <div>
                      <figcaption className="text-sm font-bold text-foreground-950">{t.name}</figcaption>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 联系我们 */}
      <section className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <p className="text-sm font-bold tracking-wide text-primary-600">联系我们</p>
            <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-4xl">有想追的瓜？说来听听</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
              无论你想提议一个值得做成连续剧的新系列、补充某部系列的剧情细节，还是给我们反馈建议，
              都欢迎写信或通过下方表单告诉我们。我们会认真阅读每一封来信。
            </p>
            <div className="mt-7 flex flex-col gap-3">
              <a
                href="mailto:hello@chiguameiri.com"
                className="flex items-center gap-3 rounded-2xl border border-background-200 bg-background-100/70 px-5 py-4 transition-colors hover:border-primary-200"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500 text-xl text-background-50">
                  <i className="ri-mail-send-line"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-400">合作与投稿邮箱</span>
                  <span className="mt-0.5 block text-sm font-bold text-foreground-900">hello@chiguameiri.com</span>
                </span>
              </a>
              <Link
                to="/chasing"
                className="flex items-center gap-3 rounded-2xl border border-background-200 bg-background-100/70 px-5 py-4 transition-colors hover:border-primary-200"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-500 text-xl text-background-50">
                  <i className="ri-flashlight-line"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-400">先逛一逛</span>
                  <span className="mt-0.5 block text-sm font-bold text-foreground-900">去追更大厅看看正在追的系列</span>
                </span>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-background-200 bg-background-50 p-6 md:p-8">
              <h3 className="text-lg font-bold text-foreground-950">意见与线索投稿</h3>
              <p className="mt-1.5 text-sm text-foreground-500">留下想追更的系列或想补充的剧情，我们会尽快跟进。</p>
              <div className="mt-6">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}